import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Tabs,
  Tag,
  Space,
  Button,
  Typography,
  Row,
  Col,
  Statistic,
  Input,
  theme,
} from "antd";
import {
  SearchOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { makePagination } from "@components/common/TablePagination";
import dayjs from "dayjs";

const { Title, Text } = Typography;

export default function TicketsSummary() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState(10);
  const [activeTab, setActiveTab] = useState("open");
  const [searchText, setSearchText] = useState("");

  // No backend API for tickets yet - will be populated when Zendesk integration is implemented
  const allTickets = [];

  const filteredTickets = allTickets.filter((ticket) => {
    const matchesTab = ticket.status === activeTab;
    const matchesSearch =
      !searchText ||
      ticket.id.toLowerCase().includes(searchText.toLowerCase()) ||
      ticket.title.toLowerCase().includes(searchText.toLowerCase()) ||
      ticket.systemSn.toLowerCase().includes(searchText.toLowerCase()) ||
      ticket.customer.toLowerCase().includes(searchText.toLowerCase());
    return matchesTab && matchesSearch;
  });

  const getPriorityTag = (priority) => {
    const colors = { high: "red", medium: "orange", low: "blue" };
    return <Tag color={colors[priority]}>{priority.toUpperCase()}</Tag>;
  };

  const getStatusTag = (status) => {
    const config = {
      open: { color: "error", icon: <ExclamationCircleOutlined /> },
      ongoing: { color: "processing", icon: <ClockCircleOutlined /> },
      closed: { color: "success", icon: <CheckCircleOutlined /> },
    };
    return (
      <Tag color={config[status].color} icon={config[status].icon}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Tag>
    );
  };

  const columns = [
    {
      title: "Ticket ID",
      dataIndex: "id",
      key: "id",
      render: (id) => (
        <Button type="link" onClick={() => navigate(`/tickets/${id}`)} style={{ padding: 0 }}>
          {id}
        </Button>
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      ellipsis: true,
    },
    {
      title: "System",
      dataIndex: "systemSn",
      key: "systemSn",
      render: (sn) => (
        <Button type="link" onClick={() => navigate(`/systems/${sn}`)} style={{ padding: 0 }}>
          {sn}
        </Button>
      ),
    },
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      render: getPriorityTag,
      width: 100,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: getStatusTag,
      width: 110,
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 140,
    },
    {
      title: "Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 140,
    },
    {
      title: "Assignee",
      dataIndex: "assignee",
      key: "assignee",
      render: (assignee) => assignee || <Text type="secondary">Unassigned</Text>,
    },
  ];

  const stats = {
    open: allTickets.filter((t) => t.status === "open").length,
    ongoing: allTickets.filter((t) => t.status === "ongoing").length,
    closed: allTickets.filter((t) => t.status === "closed").length,
    highPriority: allTickets.filter((t) => t.priority === "high" && t.status !== "closed").length,
  };

  const tabItems = [
    {
      key: "open",
      label: (
        <Space>
          <ExclamationCircleOutlined />
          Open
          <Tag color="error">{stats.open}</Tag>
        </Space>
      ),
    },
    {
      key: "ongoing",
      label: (
        <Space>
          <ClockCircleOutlined />
          Ongoing
          <Tag color="processing">{stats.ongoing}</Tag>
        </Space>
      ),
    },
    {
      key: "closed",
      label: (
        <Space>
          <CheckCircleOutlined />
          Closed
          <Tag color="success">{stats.closed}</Tag>
        </Space>
      ),
    },
  ];

  return (
    <PageLayout active="ticketsSummary">
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <Title level={2} style={{ marginBottom: 4 }}>
              Tickets
            </Title>
            <Text type="secondary" style={{ fontSize: 16 }}>
              Manage support tickets and customer issues
            </Text>
          </div>
          <Button type="primary" icon={<PlusOutlined />} size="large">
            New Ticket
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Open Tickets"
              value={stats.open}
              valueStyle={{ color: token.colorError }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Ongoing"
              value={stats.ongoing}
              valueStyle={{ color: token.colorPrimary }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Closed (This Month)"
              value={stats.closed}
              valueStyle={{ color: token.colorSuccess }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="High Priority"
              value={stats.highPriority}
              valueStyle={{ color: "#ff4d4f" }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Tickets Table */}
      <Card
        bordered={false}
        style={{ borderRadius: 12 }}
        styles={{
          body: { padding: 0 },
        }}
      >
        <div style={{ padding: "16px 24px", borderBottom: `1px solid ${token.colorBorderSecondary}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={tabItems}
              style={{ marginBottom: -17 }}
            />
            <Input
              placeholder="Search tickets..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
              allowClear
            />
          </div>
        </div>
        <Table
          columns={columns}
          dataSource={filteredTickets}
          rowKey="id"
          pagination={makePagination(pageSize, setPageSize)}
          style={{ padding: "0 24px 24px" }}
        />
      </Card>
    </PageLayout>
  );
}
